import { cn } from '@/lib/utils';
import { APP_STORES } from '@/lib/brand';

interface Props {
    className?: string;
    size?: 'sm' | 'md';
}

const GooglePlayGlyph = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <defs>
            <linearGradient id="gp-a" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#00d4ff" />
                <stop offset="1" stopColor="#0094ff" />
            </linearGradient>
            <linearGradient id="gp-b" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#ffd400" />
                <stop offset="1" stopColor="#ff9800" />
            </linearGradient>
            <linearGradient id="gp-c" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#ff3b3b" />
                <stop offset="1" stopColor="#e21919" />
            </linearGradient>
            <linearGradient id="gp-d" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#00e676" />
                <stop offset="1" stopColor="#00b34a" />
            </linearGradient>
        </defs>
        <path d="M6 4.2 C5.3 4.7 5 5.5 5 6.5 V33.5 C5 34.5 5.3 35.3 6 35.8 L21 20 Z" fill="url(#gp-a)" />
        <path d="M27 14 L6 4.2 L21 20 Z" fill="url(#gp-d)" />
        <path d="M27 26 L21 20 L6 35.8 Z" fill="url(#gp-c)" />
        <path d="M35 18.8 C36 19.4 36 20.6 35 21.2 L27 26 L21 20 L27 14 Z" fill="url(#gp-b)" />
    </svg>
);

const AppStoreGlyph = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" aria-hidden>
        <path d="M17.564 12.727c-.028-3.057 2.497-4.52 2.611-4.593-1.423-2.081-3.637-2.368-4.425-2.4-1.88-.191-3.67 1.11-4.625 1.11-.957 0-2.423-1.083-3.981-1.053-2.05.031-3.94 1.19-5 3.027-2.132 3.694-.544 9.15 1.528 12.144 1.012 1.467 2.22 3.116 3.807 3.057 1.528-.062 2.103-.99 3.95-.99 1.846 0 2.36.99 3.977.956 1.641-.031 2.682-1.496 3.686-2.97 1.162-1.706 1.64-3.355 1.669-3.44-.036-.016-3.203-1.228-3.232-4.872-.024-3.048 2.488-4.51 2.603-4.586zM14.584 3.75c.843-1.022 1.414-2.437 1.258-3.846-1.216.05-2.685.81-3.557 1.83-.78.905-1.466 2.352-1.283 3.731 1.355.105 2.739-.688 3.582-1.715z" />
    </svg>
);

/** Google Play + App Store badges. URLs + copy pulled from `APP_STORES` in `@/lib/brand`. */
const StoreBadges = ({ className, size = 'md' }: Props) => {
    const height = size === 'sm' ? 'h-10' : 'h-12';
    const iconSize = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
    const labelSize = size === 'sm' ? 'text-[9px]' : 'text-[10px]';
    const titleSize = size === 'sm' ? 'text-xs' : 'text-sm';
    const badgeCls = cn(
        'flex items-center gap-2.5 px-4 rounded-xl',
        'bg-black/60 hover:bg-black/80 backdrop-blur-sm border border-white/10 hover:border-white/20',
        'transition-all duration-300 group shadow-lg hover:shadow-xl',
        height
    );

    return (
        <div className={cn('flex gap-3 flex-wrap', className)}>
            <a
                href={APP_STORES.googlePlay.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Get it on ${APP_STORES.googlePlay.labelEn}`}
                className={badgeCls}
            >
                <GooglePlayGlyph className={iconSize} />
                <div className="flex flex-col items-start leading-none">
                    <span className={cn('text-white/60 font-semibold', labelSize)}>{APP_STORES.googlePlay.captionEn}</span>
                    <span className={cn('text-white font-black tracking-tight', titleSize)}>{APP_STORES.googlePlay.labelEn}</span>
                </div>
            </a>

            <a
                href={APP_STORES.appStore.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Download on the ${APP_STORES.appStore.labelEn}`}
                className={badgeCls}
            >
                <AppStoreGlyph className={iconSize} />
                <div className="flex flex-col items-start leading-none">
                    <span className={cn('text-white/60 font-semibold', labelSize)}>{APP_STORES.appStore.captionEn}</span>
                    <span className={cn('text-white font-black tracking-tight', titleSize)}>{APP_STORES.appStore.labelEn}</span>
                </div>
            </a>
        </div>
    );
};

export default StoreBadges;
